/* eslint-disable @typescript-eslint/no-explicit-any */

import z from "zod";

type Migration = {
  dataTransform: (data: any) => any;
};

type SchemaVersion = {
  schema: z.ZodType;
  version: number;
};

export interface Entity<T extends z.ZodType> {
  name: string;
  schema: T;
  migrations: Migration[];
  migrate<U extends z.ZodType>(
    schemaTransform: (schema: T) => U,
    dataTransform: (data: z.infer<T>) => z.infer<U>,
  ): Entity<U>;
  migrateData(data: any): z.infer<T>;
  parse(data: unknown): z.infer<T>;
}

export interface MigrationManager<T extends z.ZodType> {
  schema: T;
  migrate(data: any): z.infer<T>;
  addMigration<U extends z.ZodType>(
    schemaTransform: (schema: T) => U,
    dataTransform: (data: z.infer<T>) => z.infer<NoInfer<U>>,
  ): MigrationManager<U>;
}

function _createMigrationManager<T extends z.ZodType>(
  schema: T,
  migrations: Migration[] = [],
  schemaVersions: SchemaVersion[] = [],
): MigrationManager<T> {
  const allVersions =
    schemaVersions.length === 0 ? [{ schema, version: 0 }] : schemaVersions;

  function detectVersion(data: any): number {
    // Work backwards from latest version to oldest
    for (let i = allVersions.length - 1; i >= 0; i--) {
      const version = allVersions[i];
      if (version && version.schema.safeParse(data).success) {
        return version.version;
      }
    }
    // If no version matches, assume oldest version
    return 0;
  }

  function applyMigrationsFromVersion(
    data: any,
    fromVersion: number,
  ): z.infer<T> {
    let result = structuredClone(data);

    // Apply only migrations from the detected version forward
    for (let i = fromVersion; i < migrations.length; i++) {
      const migration = migrations[i];
      if (migration) {
        result = migration.dataTransform(result);
      }
    }

    return result;
  }

  return {
    schema,
    migrate(data: any): z.infer<T> {
      const detectedVersion = detectVersion(data);
      const migratedData = applyMigrationsFromVersion(data, detectedVersion);

      // Validate the final result against the latest schema
      return schema.parse(migratedData);
    },
    addMigration<U extends z.ZodType>(
      schemaTransform: (schema: T) => U,
      dataTransform: (data: z.infer<T>) => z.infer<NoInfer<U>>,
    ) {
      const newSchema = schemaTransform(schema);
      const newMigrations = [...migrations, { dataTransform }];
      const newVersions = [
        ...allVersions,
        { schema: newSchema, version: allVersions.length },
      ];
      return _createMigrationManager(newSchema, newMigrations, newVersions);
    },
  };
}

export function createMigrationManager<T extends z.ZodType>(
  schema: T,
): MigrationManager<T> {
  return _createMigrationManager(schema, []);
}
